import {
  faCircleExclamation,
  faClipboard,
  faDownload,
  faMagnifyingGlass,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { Alert, DatePicker, Input } from "antd";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import moment from "moment";
import { useMemo, useState } from "react";
import { Button, Col, Modal, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import Loader from "../../../components/Loader";
import Message from "../../../components/Message";
import { calculateMaidStats } from "../../../controllers/reports";
import usePermissions from "../../../hook/userPermission";

const { RangePicker } = DatePicker;

const SummarizedReports = ({ toDownload, isAdmin, maids, type }) => {
  const { hasPermission } = usePermissions();
  const [dateRange, setDateRange] = useState([null, null]);
  const [serviceName, setServiceName] = useState("");
  const [filteredMaidListData, setFilteredMaidListData] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedMaid, setSelectedMaid] = useState(null);
  const [modalPagination, setModalPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const maidEarnings = useSelector((state) => state.maidEarnings);
  const {
    loading: filteredLoading,
    earnings,
    error: filteredError,
  } = maidEarnings;

  const requiredMaidEarnings = earnings.filter((maid) => maid.type == type);

  const sortTipsByLatest = (tips) => {
    return [...tips].sort((a, b) => new Date(b?.date) - new Date(a?.date));
  };

  const sortedTips = useMemo(() => {
    if (!selectedMaid) return [];

    let tips = selectedMaid.tips || [];

    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = new Date(dateRange[0]);
      const endDate = new Date(dateRange[1]);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      tips = tips.filter((tip) => {
        const tipDate = new Date(tip.date);
        return tipDate >= startDate && tipDate <= endDate;
      });
    }

    return sortTipsByLatest(tips);
  }, [selectedMaid, dateRange]);
  // const sortedTips = useMemo(() => {
  //   return sortTipsByLatest(selectedMaid?.tips || []);
  // }, [selectedMaid?.tips]);

  // const handleDateRangeChange = (dates) => {
  //   setDateRange(dates || [null, null]);
  //   if (!dates || !dates[0] || !dates[1]) {
  //     maids.forEach((maid) => {
  //       maid.totalTipsInRange = null;
  //     });
  //   }
  // };

  const handleDateRangeChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      setDateRange([null, null]);
      setFilteredMaidListData([]);
      setNoResults(false);
      return;
    }

    setDateRange(dates);
  };

  const handleServiceNameFilter = (e) => {
    setServiceName(e.target.value);
  };

  const handleFilter = () => {
    console.log("======== FILTER START ========");

    if (!maids || maids.length === 0) {
      console.log("❌ No maids available");
      return;
    }

    console.log("Total maids:", maids.length);
    console.log("Selected dateRange:", dateRange);
    console.log("Service name filter:", serviceName);

    const filteredList = maids
      .map((maid) => {
        console.log("--------------");
        console.log("Processing maid:", maid.firstName, maid.lastName);
        console.log("Service name:", maid.serviceName);

        const maidCopy = { ...maid };

        /* SERVICE NAME FILTER */
        if (
          serviceName &&
          !maidCopy.serviceName
            ?.toLowerCase()
            .includes(serviceName.toLowerCase())
        ) {
          console.log("❌ Service name mismatch");
          return null;
        }

        console.log("✔ Service name matched");

        /* DATE RANGE FILTER */
        if (dateRange && dateRange[0] && dateRange[1]) {
          const startDate = new Date(dateRange[0]);
          const endDate = new Date(dateRange[1]);

          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);

          console.log("Start date:", startDate);
          console.log("End date:", endDate);

          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            console.log("❌ Invalid date range");
            return null;
          }

          console.log("Total tips:", maidCopy.tips?.length || 0);

          const tipsInRange = (maidCopy.tips || []).filter((tip) => {
            const tipDate = new Date(tip.date);
            const isValid = tipDate >= startDate && tipDate <= endDate;

            console.log("Tip date raw:", tip.date);
            console.log("Parsed tip date:", tipDate);
            console.log("Amount:", tip.amount);
            console.log("Inside range:", isValid);

            return isValid;
          });

          console.log("Tips in range:", tipsInRange.length);

          if (!tipsInRange.length) {
            console.log("❌ No tips found in date range");
            return null;
          }

          maidCopy.filteredTips = tipsInRange;

          maidCopy.totalTipsInRange = Number(
            tipsInRange
              .reduce((sum, tip) => sum + Number(tip.amount || 0), 0)
              .toFixed(2)
          );

          maidCopy.totalReviewsInRange = tipsInRange.length;

          console.log("✔ Earnings in range:", maidCopy.totalTipsInRange);
          console.log("✔ Reviews in range:", maidCopy.totalReviewsInRange);
        }

        console.log("✔ Maid passed filters:", maidCopy.firstName);

        return maidCopy;
      })
      .filter(Boolean);

    console.log("======== FILTER RESULT ========");
    console.log("Filtered maids count:", filteredList.length);
    console.log("Filtered maids:", filteredList);

    setFilteredMaidListData(filteredList);
    setNoResults(filteredList.length === 0);

    console.log("======== FILTER END ========");
  };

  const handleDownloadExcel = () => {
    const dataToExport =
      filteredMaidListData.length > 0 &&
      (serviceName || (dateRange && dateRange[0] && dateRange[1]))
        ? filteredMaidListData
        : maids;

    const data = dataToExport.map((maid) => ({
      "Employ ID": maid.employId,
      Name: `${maid.firstName} ${maid.lastName}`,
      "Service Name": maid.serviceName,
      "Total Earnings":
        maid.totalTipsInRange || calculateMaidStats(maid).totalEarnings,
      "Total Reviews":
        maid.totalReviewsInRange || calculateMaidStats(maid).totalReviews,
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summarized Reports");
    XLSX.writeFile(workbook, "summarized-reports.xlsx");
  };

  const handleModal = (maid) => {
    setSelectedMaid(maid);
    setShowModal(!showModal);
    setModalPagination({ pageIndex: 0, pageSize: 10 }); // Reset pagination when modal opens
  };

  // Modal table configuration
  const modalColumns = useMemo(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        Cell: ({ cell }) => moment(cell.getValue()).format("MM/DD/YYYY"),
        size: 100,
      },
      {
        accessorKey: "paymentId",
        header: "Transaction ID",
        size: 200,
      },
      {
        accessorKey: "account",
        header: "Account ID",
        size: 150,
      },
      {
        accessorKey: "amount",
        header: "Tip",
        Cell: ({ cell }) => `$${cell.getValue()}`,
        size: 80,
      },
      {
        accessorKey: "review",
        header: "Review",
        size: 200,
      },
    ],
    []
  );

  const modalTable = useMaterialReactTable({
    columns: modalColumns,
    data: sortedTips,
    initialState: {
      density: "compact",
    },
    manualPagination: false,
    state: {
      pagination: modalPagination,
    },
    onPaginationChange: setModalPagination,
    enableTopToolbar: false,
    enableStickyHeader: true,
    enableColumnFilters: false,
    enableColumnOrdering: false,
    enableSorting: false,
    enableColumnActions: false,
    muiTablePaperProps: {
      elevation: 0,
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        color: "#333",
      },
    },
  });

  return (
    <>
      {/* MAID REPORT MODAL */}
      <Modal
        backdrop="static"
        centered
        show={showModal}
        onHide={handleModal}
        size="xl"
      >
        <Modal.Header className="bg-primary text-light">
          <Modal.Title>{selectedMaid?.firstName}'s Earnings</Modal.Title>
          <Button
            variant="outline-light"
            className="border-0"
            onClick={() => setShowModal(false)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </Button>
        </Modal.Header>
        <Modal.Body className="report">
          {selectedMaid && (
            <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
              <MaterialReactTable table={modalTable} />
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* MAIN COMPONENT */}
      <Row className="px-3">
        <Col>
          {noResults && (
            <Alert
              message="No results found"
              type="error"
              showIcon
              closable
              onClose={() => setNoResults(false)}
            />
          )}

          {/* FILTERS */}
          <Row className="my-4 align-items-center justify-content-center">
            <Col xs="auto">
              <RangePicker
                className="py-2"
                value={dateRange}
                onChange={handleDateRangeChange}
                placeholder={["Start Date", "End Date"]}
              />
            </Col>
            <Col xs={4}>
              <Input
                className="py-2"
                placeholder="Service Name"
                value={serviceName}
                onChange={handleServiceNameFilter}
                allowClear
              />
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-secondary"
                onClick={handleFilter}
                className="px-3 mx-1"
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </Button>
              {hasPermission("toDownload") && (
                <Button
                  variant="outline-secondary"
                  onClick={handleDownloadExcel}
                  className="px-3 mx-1"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </Button>
              )}
            </Col>
          </Row>

          {/* MAIN TABLE */}
          <Row>
            <Col className="p-2 border border-1 rounded-1">
              {filteredLoading ? (
                <Loader />
              ) : filteredError ? (
                <Message>{"Message"}</Message>
              ) : (
                <MaterialReactTable
                  columns={[
                    {
                      accessorKey: "employId",
                      header: "ID",
                      size: 100,
                    },
                    {
                      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
                      header: "Name",
                      size: 150,
                    },
                    {
                      accessorKey: "serviceName",
                      header: "@",
                      size: 120,
                      Cell: ({ cell }) => (
                        <span className="text-success">{cell.getValue()}</span>
                      ),
                    },
                    {
                      accessorFn: (row) =>
                        row.totalTipsInRange ||
                        calculateMaidStats(row).totalEarnings,
                      header: "Earnings",
                      size: 100,
                      Cell: ({ cell }) => `$${cell.getValue()}`,
                    },
                    {
                      accessorFn: (row) =>
                        row.totalReviewsInRange ||
                        calculateMaidStats(row).totalReviews,
                      header: "Reviews",
                      size: 100,
                    },
                    {
                      id: "actions",
                      header: "Actions",
                      size: 150,
                      Cell: ({ row }) => (
                        <Button
                          className="p-1 px-2 border-0"
                          variant="outline-success"
                          onClick={() => handleModal(row.original)}
                        >
                          <FontAwesomeIcon
                            icon={faClipboard}
                            className="me-2"
                          />
                          View
                        </Button>
                      ),
                    },
                  ]}
                  data={
                    filteredMaidListData.length > 0 &&
                    (serviceName || (dateRange[0] && dateRange[1]))
                      ? filteredMaidListData
                      : maids
                  }
                  initialState={{
                    density: "compact",
                    enableTopToolbar: false,
                  }}
                  enableStickyHeader={true}
                  enableColumnFilters={false}
                  enableColumnOrdering={false}
                  enableSorting={false}
                  enableColumnActions={false}
                  enableTopToolbar={false}
                  muiTablePaperProps={{
                    elevation: 0,
                  }}
                  muiTableHeadRowProps={{
                    sx: {
                      backgroundColor: "#f5f5f5",
                      color: "#333",
                    },
                  }}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default SummarizedReports;
